import type { AppRouter } from '@risksmart-app/trpc/src/routers/router';
import { QueryCache, QueryClient } from '@tanstack/react-query';
import { createTRPCContext } from '@trpc/tanstack-react-query';

import { handleError } from './errorUtils';

let browserQueryClient: QueryClient | undefined;

const makeQueryClient = () => {
  return new QueryClient({
    // tanstack-query swallows query errors unless a consumer reads isError —
    // mirror the Apollo errorLink (ApolloGraphqlProvider) so silent failures
    // still reach Sentry. Mutations are excluded: FormContext owns mutation
    // error display and reporting (RSP-6539).
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Only the procedure path (queryKey[0]) is captured — the full key
        // embeds the procedure input (entity ids, filter selections), which
        // would widen what reaches Sentry beyond the Apollo errorLink's
        // operationName parity.
        handleError(error, {
          tags: { source: 'trpc-query' },
          extra: { procedurePath: query.queryKey[0] },
        });
      },
    }),
    defaultOptions: {
      queries: {
        // Stale-while-revalidate defaults (COI-451):
        // - staleTime stays 0, so EVERY mount still triggers a network
        //   refetch — we never trust cached data without revalidating it.
        //   Read-mostly hooks can opt in to real caching via the existing
        //   per-hook `trpcStaleTime` knob (see hook-factories/types.ts).
        // - gcTime > 0 keeps the last response around so remounts render
        //   the previous data instantly while the refetch is in flight,
        //   instead of a blank loading state (the Apollo 'no-cache'
        //   emulation this replaces threw all data away on unmount).
        // The QueryClient is cleared on auth changes (TrpcProvider), so
        // cached data never crosses session/org boundaries.
        gcTime: 5 * 60 * 1000, // Keep unused query data for 5 minutes
        staleTime: 0, // Always consider data stale => refetch on mount
        refetchOnMount: true, // Always refetch when component mounts
        refetchOnWindowFocus: false, // Don't refetch on window focus
      },
    },
  });
};

export const getQueryClient = () => {
  if (!browserQueryClient) {
    browserQueryClient = makeQueryClient();
  }

  return browserQueryClient;
};

export const { TRPCProvider, useTRPC, useTRPCClient } =
  createTRPCContext<AppRouter>();
